'use client';

import { ChevronLeft, BookOpen, MessageCircleHeart, Clock, Gift, Wrench, Hand } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { LoveLanguage } from '@/types';
import { Link } from '@/i18n/navigation';

const menuItems = [
  {
    id: LoveLanguage.A,
    icon: MessageCircleHeart,
    gradient: 'from-amber-500 to-orange-500',
    borderHover: 'hover:border-amber-400 dark:hover:border-amber-500',
  },
  {
    id: LoveLanguage.B,
    icon: Clock,
    gradient: 'from-teal-500 to-cyan-500',
    borderHover: 'hover:border-teal-400 dark:hover:border-teal-500',
  },
  {
    id: LoveLanguage.C,
    icon: Gift,
    gradient: 'from-rose-500 to-pink-500',
    borderHover: 'hover:border-rose-400 dark:hover:border-rose-500',
  },
  {
    id: LoveLanguage.D,
    icon: Wrench,
    gradient: 'from-blue-500 to-indigo-500',
    borderHover: 'hover:border-blue-400 dark:hover:border-blue-500',
  },
  {
    id: LoveLanguage.E,
    icon: Hand,
    gradient: 'from-orange-500 to-red-500',
    borderHover: 'hover:border-orange-400 dark:hover:border-orange-500',
  },
];

export default function GuideMenuClient() {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const isRTL = locale === 'ar';

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-8 relative" data-testid="info-menu">
      <div className="absolute inset-0 bg-mesh-gradient pointer-events-none" />

      <div className="relative z-10">
        <div className="flex items-center gap-4 mb-10" data-testid="info-menu-header">
          <button
            onClick={() => router.push('/')}
            className="p-2.5 rounded-xl hover:bg-secondary transition-all cursor-pointer"
            data-testid="info-menu-back"
            aria-label={t('btn_back')}
          >
            <ChevronLeft className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
          <div className="flex items-center gap-3" data-testid="info-menu-title">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
                {t('info_menu_title')}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">{t('info_menu_subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-4" data-testid="info-menu-grid">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.id}
                href={`/guide/${item.id}`}
                className={`group relative overflow-hidden bg-card border border-border ${item.borderHover} rounded-2xl p-5 transition-all duration-300 card-elevated hover:card-glow text-left rtl:text-right flex items-center gap-5 cursor-pointer opacity-0 animate-reveal-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`info-menu-item-${item.id}`}
              >
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br ${item.gradient} transition-transform group-hover:scale-110`}
                  data-testid={`info-menu-icon-${item.id}`}
                >
                  <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2
                    className="font-display text-lg md:text-xl font-semibold text-foreground mb-1 group-hover:text-primary transition-colors"
                    data-testid={`info-menu-item-title-${item.id}`}
                  >
                    {t(`lang_${item.id}`)}
                  </h2>
                  <p
                    className="text-sm text-foreground/60 line-clamp-2 leading-relaxed"
                    data-testid={`info-menu-item-desc-${item.id}`}
                  >
                    {t(`desc_${item.id}`)}
                  </p>
                </div>
                <ChevronLeft
                  className={`w-5 h-5 text-foreground/40 group-hover:text-primary transition-all group-hover:translate-x-1 ${isRTL ? '' : 'rotate-180'}`}
                />
              </Link>
            );
          })}
        </div>

        <div
          className="mt-10 p-5 bg-secondary/50 rounded-2xl border border-border"
          data-testid="info-menu-science"
        >
          <p className="text-sm text-foreground/70 leading-relaxed">
            <span className="font-semibold text-foreground">{t('info_science_note_title')}</span>{' '}
            {t('info_science_note')}
          </p>
        </div>
      </div>
    </div>
  );
}
